/**
 * Sending a customer on, and saying where they land.
 *
 * The transfer panel used to have one question — which service — and one answer
 * the server gave for it: keep the customer's place. A clerk now has three places
 * to send somebody and four ways to place them, and the rules about which go
 * together are the server's. They are mirrored here, where a test runs them,
 * because a screen that offers a combination the server refuses is a screen that
 * teaches the clerk its buttons cannot be trusted.
 *
 * - A **service** is a transfer: the customer joins that service's line. Their
 *   place is kept by default, because they have already waited once.
 * - A **desk** or a **colleague** is a park into that desk's or that person's pool:
 *   the same piece of work, finished by somebody else. The pool's clock starts at
 *   the park, so there is no place to keep, and "keep" is not offered.
 */
import { t, type StaffLanguage } from './staffText'
import type { CounterView, Ticket } from './types'

export type SendTarget = 'service' | 'desk' | 'colleague'

export const SEND_TARGETS: readonly SendTarget[] = ['service', 'desk', 'colleague']

/** issued: keep their place by time issued (lines only). */
export type Placement = 'keep' | 'issued' | 'front' | 'back' | 'delay'

/** The shortest and longest a customer may be held, in minutes: the server's
 *  sixty seconds and one hour, as the screen asks for them. */
export const DELAY_MIN_MINUTES = 1
export const DELAY_MAX_MINUTES = 60

export function targetLabel(target: SendTarget, lang: StaffLanguage): string {
  return t(`floor.target.${target}`, lang)
}

/** The placements a target offers, its default first. */
export function placementsFor(target: SendTarget): Placement[] {
  return target === 'service' ? ['keep', 'issued', 'front', 'back', 'delay'] : ['back', 'front', 'delay']
}

/** The counter's Transfer offers only these, for every target. The server still
 *  takes keep, issued and delay. */
export const COUNTER_PLACEMENTS: readonly Placement[] = ['front', 'back']

/** The counter's default: the back of the line (owner, 31 Oct). */
export const COUNTER_DEFAULT_PLACEMENT: Placement = 'back'

export function placementLabel(placement: Placement, lang: StaffLanguage): string {
  return t(`floor.place.${placement}`, lang)
}

/** A placement the chosen target does not offer falls back to its default,
 *  rather than being sent and refused. The case is ordinary: the panel remembers
 *  "keep" from a transfer, and the clerk then switches the target to a desk. */
export function placementToSend(chosen: Placement, target: SendTarget): Placement {
  const offered = placementsFor(target)
  return offered.includes(chosen) ? chosen : offered[0]!
}

/** Why a delay cannot be sent, or '' when it can. Whole minutes, because that is
 *  what the field asks for and a fraction is a typing mistake. */
export function whyNotDelay(minutes: number, lang: StaffLanguage): string {
  if (!Number.isInteger(minutes) || minutes < DELAY_MIN_MINUTES || minutes > DELAY_MAX_MINUTES) {
    return t('floor.holdRange', lang, { min: DELAY_MIN_MINUTES, max: DELAY_MAX_MINUTES })
  }
  return ''
}

/** What the server is sent for a placement. Nothing at all for the target's
 *  default, so that a clerk who changes nothing sends exactly what every client
 *  sent before there was a choice. */
export function placementBody(
  placement: Placement,
  target: SendTarget,
  minutes: number,
): { placement?: Placement; delaySec?: number } {
  const sent = placementToSend(placement, target)
  if (sent === 'delay') return { placement: 'delay', delaySec: minutes * 60 }
  if (sent === placementsFor(target)[0]) return {}
  return { placement: sent }
}

/** The request that sends a customer to a target. A service is a transfer; a desk
 *  or a colleague is a park into their pool — see the top of this file. */
export type SendRequest =
  | { action: 'transfer'; body: { serviceId: string; placement?: Placement; delaySec?: number } }
  | {
      action: 'park'
      body: {
        scope: 'desk' | 'staff'
        counterId?: string
        staffId?: string
        placement?: Placement
        delaySec?: number
        returnToMe?: true
      }
    }

/** `returnToMe` (Send back to me) is sent only when asked for, and only on a send
 *  to a desk or a colleague; a transfer carries it through cashier.transferBody. */
export function sendRequest(
  target: SendTarget,
  destinationId: string,
  placement: Placement,
  minutes: number,
  returnToMe = false,
): SendRequest {
  const placed = placementBody(placement, target, minutes)
  const back = returnToMe ? { returnToMe: true as const } : {}
  switch (target) {
    case 'service':
      return { action: 'transfer', body: { serviceId: destinationId, ...placed } }
    case 'desk':
      return { action: 'park', body: { scope: 'desk', counterId: destinationId, ...placed, ...back } }
    case 'colleague':
      return { action: 'park', body: { scope: 'staff', staffId: destinationId, ...placed, ...back } }
  }
}

/** A person a customer can be sent to, as GET /branches/{id}/colleagues answers. */
export interface Colleague {
  id: string
  displayName: string
}

/** The desks a customer may be sent to: every desk in the branch but this one,
 *  open or not. A closed desk is offered on purpose — the customer waits in its
 *  pool until somebody signs in there, and the floor shows them waiting — because
 *  refusing it would send the clerk looking for a colleague who is on a break. */
export function deskChoices(counters: readonly CounterView[], selfCounterId: string): CounterView[] {
  return counters
    .filter((c) => c.counterId !== selfCounterId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
}

/** The colleagues a customer may be sent to: everybody but the clerk sending them,
 *  who would be parking the customer for themselves under another name. */
export function colleagueChoices(colleagues: readonly Colleague[], selfStaffId: string): Colleague[] {
  return colleagues.filter((c) => c.id !== selfStaffId)
}

/**
 * Who a parked customer is waiting for, in the words the operator would use.
 *
 * Named by the desk or the person, because "for Counter 02" and "for Aysel" are
 * things the person standing nearby can act on where "desk pool" is not. A person
 * is named from the colleagues list, or from the desk they are signed in at when
 * the list has not arrived.
 */
export function waitingFor(
  ticket: Ticket,
  counterId: string,
  staffId: string,
  counters: readonly CounterView[] | undefined,
  colleagues: readonly Colleague[] | undefined,
  lang: StaffLanguage,
): string {
  counters ??= []
  colleagues ??= []
  if (ticket.parkScope === 'staff') {
    if (staffId && ticket.poolStaffId === staffId) return t('floor.forYou', lang)
    const name =
      colleagues.find((c) => c.id === ticket.poolStaffId)?.displayName ??
      counters.find((c) => c.staffId && c.staffId === ticket.poolStaffId)?.staffName
    return name ? t('floor.forName', lang, { name }) : t('floor.forColleague', lang)
  }
  if (ticket.parkScope === 'desk') {
    if (counterId && ticket.poolCounterId === counterId) return t('floor.forYou', lang)
    const desk = counters.find((c) => c.counterId === ticket.poolCounterId)
    return desk ? t('floor.forName', lang, { name: desk.name }) : t('floor.forAnotherDesk', lang)
  }
  return t('floor.forAnyDesk', lang)
}
